import { useEffect, useState, useCallback } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { trackImageView } from "../../utils/analytics";
import "./ImageGallery.css";

/**
 * Composant de galerie d'images avec carousel et lightbox
 * @param {Array} images - Tableau d'objets {src, alt}
 * @param {Object} settings - Configuration optionnelle du carousel (slick-carousel)
 */
export default function ImageGallery({ images, settings = {} }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [carouselReady, setCarouselReady] = useState(false);

  // Initialiser le carousel après le premier rendu pour améliorer le LCP
  useEffect(() => {
    // Délai minimal pour permettre à la première image de s'afficher
    const timer = setTimeout(() => {
      setCarouselReady(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Configuration par défaut du carousel
  const defaultSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    arrows: true,
    lazyLoad: "progressive", // Lazy loading progressif pour le carousel
    ...settings, // Permet de surcharger les paramètres par défaut
  };

  // Ouvrir la lightbox
  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    trackImageView(images[index].alt || `Image ${index + 1}`);
  };

  // Fermer la lightbox
  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  // Navigation dans la lightbox
  const goToPrevious = useCallback(() => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1,
    );
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1,
    );
  }, [images.length]);

  // Gestion des touches clavier
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") goToPrevious();
      if (e.key === "ArrowRight") goToNext();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, closeLightbox, goToPrevious, goToNext]);

  // Empêcher le scroll du body quand la lightbox est ouverte
  useEffect(() => {
    if (lightboxOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [lightboxOpen]);

  return (
    <>
      {/* Carousel */}
      <div className="image-gallery-carousel">
        {!carouselReady ? (
          // Afficher la première image immédiatement (améliore le LCP)
          <div
            className="carousel-slide carousel-slide-first"
            onClick={() => openLightbox(0)}
          >
            <img
              src={images[0].src}
              alt={images[0].alt}
              loading="eager"
              fetchPriority="high"
              decoding="sync"
              width={images[0].width || 800}
              height={images[0].height || 600}
            />
          </div>
        ) : (
          // Carousel complet une fois initialisé
          <Slider {...defaultSettings}>
            {images.map((image, index) => (
              <div
                key={index}
                onClick={() => openLightbox(index)}
                className="carousel-slide"
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  loading={index === 0 ? "eager" : "lazy"}
                  fetchPriority={index === 0 ? "high" : "low"}
                  decoding={index === 0 ? "sync" : "async"}
                  width={image.width || 800}
                  height={image.height || 600}
                />
              </div>
            ))}
          </Slider>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          {/* Bouton fermer - en haut à droite de la fenêtre */}
          <button
            className="lightbox-close"
            onClick={closeLightbox}
            aria-label="Fermer la galerie"
          >
            ✕
          </button>

          {/* Navigation - sur les bords de la fenêtre */}
          <button
            className="lightbox-nav lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              goToPrevious();
            }}
            aria-label="Image précédente"
          >
            ‹
          </button>
          <button
            className="lightbox-nav lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
            aria-label="Image suivante"
          >
            ›
          </button>

          {/* Image courante - centrée */}
          <div
            className="lightbox-content"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={images[currentImageIndex].src}
              alt={images[currentImageIndex].alt}
              className="lightbox-image"
            />
          </div>

          {/* Compteur - en bas au centre */}
          <div className="lightbox-counter">
            {currentImageIndex + 1} / {images.length}
          </div>
        </div>
      )}
    </>
  );
}
